import { AxiosError } from 'axios';
import { left, right } from '../../../shared/core/Either';
import { Result } from '../../../shared/core/Result';
import { APIResponse } from '../../../shared/infra/api/APIResponse';
import { BaseAPI } from '../../../shared/infra/api/BaseAPI';
import {
  PasswordResetRequestDTO,
  ResetRequest,
} from '../infra/types/RequestPasswordReset.type';
import { Profile, ProfileResponse } from '../models/profile';
import { IAuthService } from './authService';
import { DeleteUserRequest } from '../infra/types/DeleteUserRequest.type';

export interface IPublicService {
  verifyUserEmailToken(token: string): Promise<APIResponse<void>>;
  resetUserPassword(
    request: PasswordResetRequestDTO,
  ): Promise<APIResponse<void>>;
  requestReset(resetRequest: ResetRequest): Promise<APIResponse<void>>;
  verifyResetToken(token?: string): Promise<APIResponse<void>>;
  getCommunityProfile(slug?: string): Promise<APIResponse<Profile>>;
  requestUserDelete(request: DeleteUserRequest): Promise<APIResponse<void>>;
}

export class PublicService extends BaseAPI implements IPublicService {
  protected onFailureExecuteImpl(error: AxiosError<any>): Promise<any> {
    throw new Error(`Method not implemented. ${error.name}`);
  }

  constructor(authService: IAuthService) {
    super(authService);
  }

  async getCommunityProfile(slug?: string): Promise<APIResponse<Profile>> {
    try {
      const response = await this.get<ProfileResponse>({
        url: `/v1/community/profile/${slug ? slug : ''}`,
        requiresToken: false,
      });
      const profile = response.data.data;

      return right(Result.ok<Profile>(profile));
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Failed');
    }
  }

  async resetUserPassword(
    request: PasswordResetRequestDTO,
  ): Promise<APIResponse<void>> {
    try {
      await this.post({
        url: '/v1/users/reset/password',
        requiresToken: false,
        payload: request,
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  async requestUserDelete(
    request: DeleteUserRequest,
  ): Promise<APIResponse<void>> {
    try {
      await this.delete({
        url: `/v1/users/deletion-request/${request.community_slug}`,
        requiresToken: false,
        payload: request,
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Failed');
    }
  }

  async requestReset(resetRequest: ResetRequest): Promise<APIResponse<void>> {
    try {
      await this.post({
        url: '/v1/users/reset-request',
        requiresToken: false,
        payload: resetRequest,
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  async verifyResetToken(token?: string): Promise<APIResponse<void>> {
    try {
      await this.get({
        url: `/v1/users/reset/verify/${token ? token : ''}`,
        requiresToken: false,
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  async verifyUserEmailToken(token?: string): Promise<APIResponse<void>> {
    try {
      await this.get({
        url: `/v1/users/verify/${token ? token : ''}`,
        requiresToken: false,
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  async verifyUserDeletionToken(token?: string): Promise<APIResponse<void>> {
    try {
      await this.get({
        url: `/v1/users/deletion/${token ? token : ''}`,
        requiresToken: false,
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }
}
